/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import {
  CargoItem,
  CARGOITEM_LOCALSTATUS_ATTRIBUTES,
} from 'src/app/_model/cargo-item';
import { IMSId, IMSObject } from 'src/app/_model/ims-object';
import {
  OrderItem,
  ORDERITEM_LOCALSTATUS_ATTRIBUTES,
} from 'src/app/_model/order-item';
import {
  Servicejob,
  SERVICEJOB_LOCALSTATUS_ATTRIBUTES,
} from 'src/app/_model/servicejob';
import { Tour, TOUR_LOCALSTATUS_ATTRIBUTES } from 'src/app/_model/tour';
import { LoggingService } from '../logging/logic/logging.service';
import { KeyValuePersistenceService } from '../persistence/key-value-persistence/key-value-persistence.service';
import { Resetable } from '../resetter/resetable';
import {
  DeficiencyTicket,
  DEFICIENCYTICKET_LOCALSTATUS_ATTRIBUTES,
} from 'src/app/_model/deficiency-ticket';

const LOCALSTATUS_SERVICEJOB = 'localstatus_servicejobs';
const LOCALSTATUS_TOUR = 'localstatus_tours';
const LOCALSTATUS_ORDERITEM = 'localstatus_orderitem';
const LOCALSTATUS_CARGOITEM = 'localstatus_cargoitem';
const LOCALSTATUS_DEFICIENCYTICKET = 'localstatus_deficiencyticket';

/**
 * Interface Definition for a LocalStatusService
 */
@Injectable({
  providedIn: 'root',
})
export class LocalStatusService implements Resetable {
  // Public Member

  // Private Member
  private _tourStatus: any;
  private _servicejobStatus: any;
  private _orderitemStatus: any;
  private _cargoitemStatus: any;
  private _deficiencyTicketStatus: any;

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private keyValueService: KeyValuePersistenceService,
  ) {}

  // Resetable
  public async reset() {
    console.log('JBLocalStatus reset');
    await this.deleteAllLocalStatusKeys();
  }
  public async clear() {
    console.log('JBLocalStatus clear');
    await this.deleteAllLocalStatusKeys();
  }

  // Public Functions

  // ServiceJob

  public async loadLocalServicejobStatus(id: IMSId): Promise<any> {
    if (id?.imsId == undefined) {
      this.logger.warn(
        this.constructor.name,
        'Do not support LocalStatus for Local Objects',
      );
      return {};
    }

    let localServicejobStatus = await this.getLocalServiceJobStatus();
    return localServicejobStatus[id.imsId];
  }

  public async storeLocalServicejobStatus(sjob: Servicejob) {
    if (sjob.id.imsId == undefined) {
      this.logger.warn(
        this.constructor.name,
        'Do not support LocalStatus for Local Objects',
      );
      return;
    }

    let localServicejobStatus = await this.getLocalServiceJobStatus();
    let localStatusObject = localServicejobStatus[sjob.id.imsId];

    let sjobLocalstatusAttributes: string[]
    if (sjob.status > 20) {
      sjobLocalstatusAttributes = SERVICEJOB_LOCALSTATUS_ATTRIBUTES;
    } else {
      // for status <= 20 we do not store status
      sjobLocalstatusAttributes = SERVICEJOB_LOCALSTATUS_ATTRIBUTES.filter(
        (attr) => attr !== 'status'
      );
    }

    let updateObject = this.updateLocalStatusObject(
      localStatusObject,
      sjobLocalstatusAttributes,
      sjob,
    );

    if (updateObject.changed) {
      localServicejobStatus[sjob.id.imsId] = updateObject.localStatusObject;
      await this.keyValueService.store(
        LOCALSTATUS_SERVICEJOB,
        localServicejobStatus,
      );
      this._servicejobStatus = localServicejobStatus;
    }
  }

  // Tour

  public async loadLocalTourStatus(id: IMSId): Promise<any> {
    if (id?.imsId == undefined) {
      this.logger.warn(
        this.constructor.name,
        'Do not support LocalStatus for Local Objects',
      );
      return {};
    }

    let localTourStatus = await this.getLocalTourStatus();
    return localTourStatus[id.imsId];
  }

  public async storeLocalTourStatus(tour: Tour) {
    if (tour.id.imsId == undefined) {
      this.logger.warn(
        this.constructor.name,
        'Do not support LocalStatus for Local Objects',
      );
      return;
    }

    let localTourStatus = await this.getLocalTourStatus();
    let localStatusObject = localTourStatus[tour.id.imsId];
    console.log('JBstoreLocalTourStatus localStatusObject', localStatusObject);
    let updateObject = this.updateLocalStatusObject(
      localStatusObject,
      TOUR_LOCALSTATUS_ATTRIBUTES,
      tour,
    );

    if (updateObject.changed) {
      localTourStatus[tour.id.imsId] = updateObject.localStatusObject;
      await this.keyValueService.store(LOCALSTATUS_TOUR, localTourStatus);
      this._tourStatus = localTourStatus;
    }
  }

  // OrderItem

  public async loadLocalOrderItemStatus(id: IMSId): Promise<any> {
    if (id?.imsId == undefined) {
      this.logger.warn(
        this.constructor.name,
        'Do not support LocalStatus for Local Objects',
      );
      return {};
    }

    let localOrderitemStatus = await this.getLocalOrderItemStatus();
    return localOrderitemStatus[id.imsId];
  }

  public async storeLocalOrderItemStatus(orderItem: OrderItem) {
    if (orderItem.id.imsId == undefined) {
      this.logger.warn(
        this.constructor.name,
        'Do not support LocalStatus for Local Objects',
      );
      return;
    }

    let localOrderitemStatus = await this.getLocalOrderItemStatus();
    let localStatusObject = localOrderitemStatus[orderItem.id.imsId];
    let updateObject = this.updateLocalStatusObject(
      localStatusObject,
      ORDERITEM_LOCALSTATUS_ATTRIBUTES,
      orderItem,
    );

    if (updateObject.changed) {
      localOrderitemStatus[orderItem.id.imsId] = updateObject.localStatusObject;
      await this.keyValueService.store(
        LOCALSTATUS_ORDERITEM,
        localOrderitemStatus,
      );
      this._orderitemStatus = localOrderitemStatus;
    }
  }

  // CargoItem

  public async loadLocalCargoItemStatus(id: IMSId): Promise<any> {
    if (id?.imsId == undefined) {
      this.logger.warn(
        this.constructor.name,
        'Do not support LocalStatus for Local Objects',
      );
      return {};
    }

    let localCargoitemStatus = await this.getLocalCargoItemStatus();
    return localCargoitemStatus[id.imsId];
  }

  public async storeLocalCargoItemStatus(cargoItem: CargoItem) {
    if (cargoItem.id.imsId == undefined) {
      this.logger.warn(
        this.constructor.name,
        'Do not support LocalStatus for Local Objects',
      );
      return;
    }

    let localCargoitemStatus = await this.getLocalCargoItemStatus();
    let localStatusObject = localCargoitemStatus[cargoItem.id.imsId];
    let updateObject = this.updateLocalStatusObject(
      localStatusObject,
      CARGOITEM_LOCALSTATUS_ATTRIBUTES,
      cargoItem,
    );

    if (updateObject.changed) {
      localCargoitemStatus[cargoItem.id.imsId] = updateObject.localStatusObject;
      await this.keyValueService.store(
        LOCALSTATUS_CARGOITEM,
        localCargoitemStatus,
      );
      this._cargoitemStatus = localCargoitemStatus;
    }
  }

  // DeficiencyTickets

  public async loadLocalDeficiencyTicketStatus(id: IMSId): Promise<any> {
    if (id?.imsId == undefined) {
      this.logger.warn(
        this.constructor.name,
        'Do not support LocalStatus for Local Objects',
      );
      return {};
    }

    let localDeficiencyTicketStatus =
      await this.getLocalDeficiencyTicketStatus();
    return localDeficiencyTicketStatus[id.imsId];
  }

  public async storeLocalDeficiencyTicketStatus(dt: DeficiencyTicket) {
    if (dt.id.imsId == undefined) {
      this.logger.warn(
        this.constructor.name,
        'Do not support LocalStatus for Local Objects',
      );
      return;
    }

    let localDeficiencyTicketStatus =
      await this.getLocalDeficiencyTicketStatus();
    let localStatusObject = localDeficiencyTicketStatus[dt.id.imsId];
    let updateObject = this.updateLocalStatusObject(
      localStatusObject,
      DEFICIENCYTICKET_LOCALSTATUS_ATTRIBUTES,
      dt,
    );

    if (updateObject.changed) {
      localDeficiencyTicketStatus[dt.id.imsId] = updateObject.localStatusObject;
      await this.keyValueService.store(
        LOCALSTATUS_DEFICIENCYTICKET,
        localDeficiencyTicketStatus,
      );
      this._deficiencyTicketStatus = localDeficiencyTicketStatus;
    }
  }

  // Private Functions

  private async deleteAllLocalStatusKeys() {
    await this.keyValueService.delete(LOCALSTATUS_SERVICEJOB);
    await this.keyValueService.delete(LOCALSTATUS_TOUR);
    await this.keyValueService.delete(LOCALSTATUS_ORDERITEM);
    await this.keyValueService.delete(LOCALSTATUS_CARGOITEM);
    await this.keyValueService.delete(LOCALSTATUS_DEFICIENCYTICKET);
    this._tourStatus = undefined;
    this._servicejobStatus = undefined;
    this._orderitemStatus = undefined;
    this._cargoitemStatus = undefined;
    this._deficiencyTicketStatus = undefined;
  }

  private async getLocalTourStatus(): Promise<any> {
    if (this._tourStatus === undefined) {
      this._tourStatus =
        (await this.keyValueService.load(LOCALSTATUS_TOUR)) || {};
    }
    return this._tourStatus;
  }

  private async getLocalServiceJobStatus(): Promise<any> {
    if (this._servicejobStatus === undefined) {
      this._servicejobStatus =
        (await this.keyValueService.load(LOCALSTATUS_SERVICEJOB)) || {};
    }
    return this._servicejobStatus;
  }

  private async getLocalOrderItemStatus(): Promise<any> {
    if (this._orderitemStatus === undefined) {
      this._orderitemStatus =
        (await this.keyValueService.load(LOCALSTATUS_ORDERITEM)) || {};
    }
    return this._orderitemStatus;
  }

  private async getLocalCargoItemStatus(): Promise<any> {
    if (this._cargoitemStatus === undefined) {
      this._cargoitemStatus =
        (await this.keyValueService.load(LOCALSTATUS_CARGOITEM)) || {};
    }
    return this._cargoitemStatus;
  }

  private async getLocalDeficiencyTicketStatus(): Promise<any> {
    if (this._deficiencyTicketStatus === undefined) {
      this._deficiencyTicketStatus =
        (await this.keyValueService.load(LOCALSTATUS_DEFICIENCYTICKET)) || {};
    }
    return this._deficiencyTicketStatus;
  }

  private updateLocalStatusObject(
    localStatusObject: any,
    localStatusAttributes: string[],
    object: IMSObject,
  ): {
    changed: boolean;
    localStatusObject: any;
  } {
    if (localStatusObject == undefined) {
      localStatusObject = {};
    }

    let changed = false;

    for (const attribut of localStatusAttributes) {
      console.log(
        'JBstoreLocalTourStatus updateLocalStatusObject for attribute',
        attribut,
        'curValue',
        localStatusObject[attribut],
        'newValue',
        object.getValueFromDatabag(attribut),
      );
      if (
        localStatusObject[attribut] !== object.getValueFromDatabag(attribut)
      ) {
        localStatusObject[attribut] = object.getValueFromDatabag(attribut);
        changed = true;
      }
    }

    return {
      changed,
      localStatusObject,
    };
  }
}
